import { useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/alert-badge";
import { ExposureTable } from "@/components/exposure-table";
import { TypedReportView } from "@/routes/incidents/TypedReportView";
import { GasReadingsPanel } from "@/routes/incidents/GasReadingsPanel";
import {
  useIncident,
  useIncidentEvents,
  useIncidentExposure,
  useIncidentGasReadings,
  useIncidentLastPositions,
  useIncidentMap,
} from "@/api/queries/incidents";
import { useIncidentTypedReport } from "@/api/queries/reports";
import { IncidentMapSnapshot } from "@/components/incident-map-snapshot";
import { useCurrentUser } from "@/api/queries/me";
import {
  humanizeIncidentType,
  incidentDisplayName,
  type IncidentDetail,
  type IncidentEvent,
} from "@/api/models";
import { formatDateTime } from "@/lib/format";
import { findSection } from "@/lib/reports";
import type { components } from "@/api/types";

type TimelineItem = { key: string; ts: string; label: string; sub?: string | null };

// Friendly labels for the AI report's operational stages. Falls back to a
// capitalized raw name for anything unmapped, so a new StageName still renders.
const STAGE_LABELS: Record<string, string> = {
  "size-up": "Size-up",
  attack: "Attack",
  search: "Search",
  vent: "Ventilation",
  overhaul: "Overhaul",
  knockdown: "Knockdown",
  other: "Other",
};

function prettyStage(name: string): string {
  return STAGE_LABELS[name] ?? name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * The after-action draft (the "narrative" typed report) breaks the incident into
 * operational stages — size-up, attack, overhaul, … — each with a start time.
 * Surface those in the Timeline tab as name + time entries, mirroring the PDF
 * report's Timeline table so the web view and the report agree. Stage notes are
 * intentionally dropped: the tab shows the sequence of stages, not the
 * explanation behind each one.
 */
function stagesFromReport(
  report: components["schemas"]["GeneratedReportRead"] | undefined,
): TimelineItem[] {
  if (!report) return [];
  const draft = findSection(report.sections, "Narrative")?.draft as
    | { stages?: { name?: unknown; start?: unknown }[] }
    | null
    | undefined;
  const rawStages = draft?.stages;
  const stages = Array.isArray(rawStages) ? rawStages : [];
  const items: TimelineItem[] = [];
  stages.forEach((s, idx) => {
    const ts = typeof s.start === "string" ? s.start : null;
    const name = typeof s.name === "string" ? s.name : null;
    if (!ts || !name) return;
    items.push({ key: `stage-${idx}`, ts, label: prettyStage(name) });
  });
  return items;
}

/**
 * A real incident always has lifecycle milestones (dispatch, clear) even when
 * the IC app posted no task events — so fold those into the timeline instead of
 * rendering an empty "No events recorded". Posted IncidentEvents and the AI
 * report's operational stages are merged in and the whole list is sorted
 * chronologically.
 */
function buildTimeline(
  incident: IncidentDetail,
  events: IncidentEvent[] | undefined,
  stages: TimelineItem[] = [],
): TimelineItem[] {
  const items: TimelineItem[] = [
    { key: "dispatched", ts: incident.dispatched_at, label: "Dispatched" },
    ...stages,
  ];
  for (const e of events ?? []) {
    items.push({
      key: e.id,
      ts: e.ts,
      label: humanizeIncidentType(e.kind) ?? e.kind,
      sub: e.task ?? e.actor_display_name ?? null,
    });
  }
  if (incident.cleared_at) {
    items.push({
      key: "cleared",
      ts: incident.cleared_at,
      label: incident.status === "finalized" ? "Finalized" : "Cleared",
    });
  }
  return items.sort((a, b) => a.ts.localeCompare(b.ts));
}

export function IncidentDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const me = useCurrentUser();
  const incident = useIncident(id);
  const events = useIncidentEvents(id);
  // The after-action stages live on the "narrative" typed report; fold them into
  // the Timeline tab so it matches the PDF report's stage breakdown.
  const report = useIncidentTypedReport(id, "narrative");
  const gas = useIncidentGasReadings(id);
  const exposure = useIncidentExposure(id);
  const map = useIncidentMap(id);
  const positions = useIncidentLastPositions(id);
  // Roster, to resolve each exposure row's user_id → a real name instead of a
  // raw UUID. Department-scoped (RLS); a firefighter since removed simply falls
  // back to a short id.
  const tz = me.data?.department?.timezone;

  if (incident.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }
  if (incident.isError || !incident.data) {
    return <p className="text-destructive">Failed to load incident.</p>;
  }
  const i = incident.data;
  const timeline = buildTimeline(i, events.data, stagesFromReport(report.data));

  return (
    <div className="space-y-6">
      {/* print:hidden — printing happens from the Report tab, and the saved PDF
          is the REPORT. The page's own heading, metadata strip and tab bar are
          the app around the document; the report's letterhead identifies it. */}
      <header className="space-y-2 print:hidden">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">{incidentDisplayName(i)}</h1>
          <StatusPill status={i.status} />
        </div>
        <dl className="grid grid-cols-1 gap-2 text-sm text-muted-foreground sm:grid-cols-3">
          <div>
            <dt className="font-medium text-foreground">Dispatched</dt>
            <dd>{formatDateTime(i.dispatched_at, tz)}</dd>
          </div>
          <div>
            <dt className="font-medium text-foreground">Cleared</dt>
            <dd>{formatDateTime(i.cleared_at ?? null, tz)}</dd>
          </div>
          <div>
            <dt className="font-medium text-foreground">Address</dt>
            <dd>{i.address ?? "—"}</dd>
          </div>
        </dl>
      </header>

      <Tabs defaultValue="overview">
        <TabsList className="print:hidden">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="gas">Gas readings</TabsTrigger>
          <TabsTrigger value="report">Report</TabsTrigger>
          <TabsTrigger value="exposure">Exposure</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div>
                  <span className="font-medium">Incident commander: </span>
                  {i.ic_display_name ?? "—"}
                </div>
                <div>
                  <span className="font-medium">Monitored firefighters: </span>
                  {i.sensor_wearer_display_name ?? "—"}
                </div>
                <div>
                  <span className="font-medium">Sensor device: </span>
                  {i.sensor_device_serial ?? "—"}
                </div>
                <div>
                  <span className="font-medium">Incident type: </span>
                  {humanizeIncidentType(i.incident_type) ?? "—"}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Map snapshot</CardTitle>
              </CardHeader>
              <CardContent>
                {map.isLoading ? (
                  <Skeleton className="aspect-square w-full" />
                ) : map.data?.url ? (
                  <IncidentMapSnapshot
                    map={map.data}
                    positions={positions.data ?? []}
                  />
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                    No map snapshot.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {events.isLoading ? (
                <Skeleton className="h-32" />
              ) : (
                <ol className="space-y-3 border-l border-border pl-4">
                  {timeline.map((t) => (
                    <li key={t.key}>
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(t.ts, tz)}
                      </div>
                      <div className="text-sm font-medium">
                        {t.label}
                        {t.sub ? (
                          <span className="font-normal text-muted-foreground">
                            {" "}
                            · {t.sub}
                          </span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gas">
          <Card>
            <CardHeader>
              <CardTitle>Gas readings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <GasReadingsPanel gas={gas} tz={tz} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="report">
          {id ? (
            <TypedReportView
              incidentId={id}
              incidentNumber={i.incident_number}
              tz={tz}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="exposure">
          <Card>
            <CardHeader>
              <CardTitle>Exposure record</CardTitle>
            </CardHeader>
            <CardContent>
              {exposure.isLoading ? (
                <Skeleton className="h-32" />
              ) : exposure.data && exposure.data.length > 0 ? (
                <ExposureTable records={exposure.data} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No exposure data captured for this incident.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

